import { getCurrentLoadUsagePercent, getDriverDeviceNumBoard } from '@/api';
import { Area } from '@ant-design/plots';
import { useQuery } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import { Card, Typography } from 'antd';
import * as echarts from 'echarts';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
const { Title } = Typography;

// 定义颜色数组，用于柱状图的不同颜色
const colors = [
  '#5470c6', // 蓝色
  '#91cc75', // 绿色
  '#fac858', // 黄色
  '#ee6666', // 红色
  '#73c0de', // 浅蓝色
  '#3ba272', // 深绿色
  '#fc8452', // 橙色
  '#9a60b4', // 紫色
];

const DriverDeviceBarChart: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const { data, loading } = useRequest(getDriverDeviceNumBoard);

  useEffect(() => {
    if (!chartRef.current) return;

    // 如果数据为null或undefined，不渲染图表
    if (data?.result === null || data?.result === undefined || !data?.result.length) {
      // 清理已存在的图表实例
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
      return;
    }

    // 确保容器有高度后再初始化图表
    const initChart = () => {
      if (!chartRef.current) return;

      const containerHeight = chartRef.current.offsetHeight;
      if (containerHeight === 0) {
        // 如果容器高度为0，延迟初始化
        setTimeout(initChart, 100);
        return;
      }

      // 初始化图表
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }

      // 处理数据
      const chartData = data.result.map((item, index) => ({
        value: item.count,
        itemStyle: {
          color: colors[index % colors.length],
        },
      }));

      const xAxisData = data.result.map((item) => item.driver_name);

      // 配置选项 - 参考 ECharts 柱状图示例
      const option: echarts.EChartsOption = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const param = Array.isArray(params) ? params[0] : params;
            return `${param.name}<br/>${param.seriesName}: ${param.value}`;
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          top: '3%',
          bottom: '15%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLabel: {
            rotate: 0, // 如果名称太长可以设置旋转角度
            interval: 0, // 显示所有标签
            fontSize: 12,
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: number) => {
              if (value >= 1000) {
                return `${(value / 1000).toFixed(0)}K`;
              }
              return value.toString();
            },
          },
        },
        series: [
          {
            name: '设备数量',
            type: 'bar',
            data: chartData,
            barWidth: '10%', // 设置柱子宽度为类目宽度的40%，让柱子更细
            itemStyle: {
              borderRadius: [8, 8, 8, 8], // 四个角都有圆角 [上左, 上右, 下右, 下左]
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          },
        ],
      };

      chartInstance.current.setOption(option);
    };

    initChart();

    // 响应式调整 - 监听窗口大小变化
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    // 使用 ResizeObserver 监听容器大小变化
    const resizeObserver = new ResizeObserver(() => {
      chartInstance.current?.resize();
    });

    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [data]);

  return (
    <Card
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' } }}
      title={
        <Title style={{ marginTop: '20px', fontSize: '16px' }} level={3}>
          状态
        </Title>
      }
      loading={loading}
    >
      {data?.result === null || data?.result === undefined || !data?.result.length ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 0',
            color: '#999',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          无数据
        </div>
      ) : (
        <div ref={chartRef} style={{ width: '100%', flex: 1, minHeight: 0 }} />
      )}
    </Card>
  );
};

function CurrentLoadUsagePercent() {
  const { data } = useQuery({
    queryKey: ['currentLoadUsagePercent'],
    queryFn: () => getCurrentLoadUsagePercent(),
    select: (data) => data?.result || {},
    enabled: true,
    refetchInterval: 1000,
  });

  const [list, setList] = useState<{ time: string; load: number }[]>([]);

  useEffect(() => {
    if (data) {
      setList([...list, { time: dayjs(data.time).format('HH:mm:ss'), load: data.load }]);
    }
  }, [data]);

  return (
    <Card
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' } }}
      title={
        <Title style={{ marginTop: '20px', fontSize: '16px' }} level={3}>
          当前系统负载率
        </Title>
      }
      // loading={loading}
    >
      <Area
        data={list}
        height={200}
        tooltip={{
          formatter: (datum) => {
            return { name: '负载率', value: `${datum.load}%` };
          },
        }}
        {...{
          xField: 'time',
          yField: 'load',
          smooth: true,
          point: {
            size: 0,
          },
          color: '#5B8FF9',
          areaStyle: () => {
            return {
              fill: 'l(0) 0:#5B8FF9 1:#E6F7FF',
            };
          },
        }}
        // style={{ marginTop: '-10px' }}
      />
    </Card>
  );
}

export default CurrentLoadUsagePercent;
